import { useState, useEffect } from "react";
import { getAuthToken } from "../utils/authHeaders";
import { 
  Search, 
  Filter, 
  Grid3x3, 
  List,
  Package,
  Package2,
  Star,
  Edit,
  X,
  Bell
} from "lucide-react";

import { Button } from "../modules/inventory/components/button";
import { Input } from "../modules/inventory/components/input";
import { Card, CardContent } from "../modules/inventory/components/card";
import { Badge } from "../modules/inventory/components/badge";
import { Checkbox } from "../modules/inventory/components/checkbox";

import { Product } from "../../../shared/types/index";

import "../styles/scoped-tailwind.css"


type ViewMode = 'grid' | 'list';

export default function InventoryManagementPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getProductType = (category: string): string => {
    if (category.includes('Dressing') || category.includes('Foam') || category.includes('Film')) return 'Dressings';
    if (category.includes('Bandage') || category.includes('Tape') || category.includes('Gauze')) return 'Bandages';
    if (category.includes('Antiseptic') || category.includes('Solution') || category.includes('Cleanser')) return 'Antiseptics';
    if (category.includes('System') || category.includes('Device') || category.includes('Tool')) return 'Devices';
    if (category.includes('Therapy') || category.includes('Chamber')) return 'Therapy';
    return 'Other';
  };

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Fetch products data
  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/manufacturer/products', {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data || []);
      } else {
        console.error('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const filteredProducts = products.filter((product: Product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategories.length === 0 || 
                           selectedCategories.includes(product.category);
    
    const matchesBrand = selectedBrands.length === 0 || 
                        selectedBrands.some(brand => product.name.toLowerCase().includes(brand.toLowerCase()));
    
    // Extract type from category for filtering
    const productType = getProductType(product.category);
    const matchesType = selectedTypes.length === 0 || 
                       selectedTypes.includes(productType);
    
    return matchesSearch && matchesCategory && matchesBrand && matchesType;
  });

  const categories = Array.from(new Set(products.map((p: Product) => p.category)));
  const brands = Array.from(new Set([
    'WoundCare Pro', 'MediSilver', 'ClearGuard', 'FoamTech', 'BioHeal',
    'VacuHeal', 'NaturalMed', 'AlgiCare', 'ScarAway', 'HydroMax',
    'FlexiWrap', '3M Medical', 'CoFlex', 'SportMed', 'FlexiGauze',
    'CleanFlow', 'SkinPrep', 'MediChem', 'Purdue Pharma', 'MediSwab',
    'WoundScope', 'SurgicalPro', 'BioPunch', 'VenoFlow', 'MediPhoto',
    'OxyHeal', 'SonicHeal'
  ]));
  const types = Array.from(new Set(products.map((p: Product) => getProductType(p.category))));

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numAmount);
  };

  const getProductImage = (product: Product) => {
    const category = product.category.toLowerCase();
    const name = product.name.toLowerCase();
    
    // Check for specific product dummy images first
    const productImageMap: Record<string, string> = {
      'hydrocolloid': 'https://images.unsplash.com/photo-1586015555751-63bb77f4322a?w=400&h=400&fit=crop&crop=center',
      'alginate': 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=400&fit=crop&crop=center',
      'foam': 'https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=400&h=400&fit=crop&crop=center',
      'silver': 'https://images.unsplash.com/photo-1632053002679-b2d2b5b9b8e5?w=400&h=400&fit=crop&crop=center',
      'npwt': 'https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=400&h=400&fit=crop&crop=center',
      'antimicrobial': 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop&crop=center',
      'collagen': 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=400&fit=crop&crop=center',
      'compression': 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=400&fit=crop&crop=center',
      'bandage': 'https://images.unsplash.com/photo-1559757175-1c1c9b50e55d?w=400&h=400&fit=crop&crop=center',
    };

    // Try to find a matching dummy image
    for (const [key, imageUrl] of Object.entries(productImageMap)) {
      if (name.includes(key) || category.includes(key)) {
        return <img src={imageUrl} alt={product.name} className="w-full h-full object-cover rounded-lg" />;
      }
    }
    
    // Return appropriate larger SVG based on product type
    if (category.includes('dressing') || category.includes('foam') || category.includes('film')) {
      return (
        <svg viewBox="0 0 64 64" className="w-full h-full text-blue-500">
          <rect x="8" y="16" width="48" height="32" rx="6" fill="currentColor" opacity="0.2"/>
          <rect x="12" y="20" width="40" height="24" rx="4" fill="currentColor" opacity="0.4"/>
          <rect x="16" y="24" width="32" height="16" rx="2" fill="currentColor"/>
          <circle cx="24" cy="32" r="3" fill="white" opacity="0.8"/>
          <circle cx="40" cy="32" r="3" fill="white" opacity="0.8"/>
          <rect x="28" y="28" width="8" height="8" rx="1" fill="white" opacity="0.6"/>
        </svg>
      );
    }
    
    if (category.includes('alginate') || category.includes('rope')) {
      return (
        <svg viewBox="0 0 64 64" className="w-full h-full text-green-500">
          <path d="M12 20 Q32 12 52 20 Q32 28 12 20" fill="currentColor" opacity="0.3"/>
          <path d="M12 28 Q32 20 52 28 Q32 36 12 28" fill="currentColor" opacity="0.5"/>
          <path d="M12 36 Q32 28 52 36 Q32 44 12 36" fill="currentColor"/>
          <path d="M12 44 Q32 36 52 44 Q32 52 12 44" fill="currentColor" opacity="0.7"/>
          <circle cx="20" cy="24" r="2" fill="white"/>
          <circle cx="44" cy="32" r="2" fill="white"/>
          <circle cx="28" cy="40" r="2" fill="white"/>
        </svg>
      );
    }
    
    if (category.includes('bandage') || category.includes('tape') || category.includes('gauze')) {
      return (
        <svg viewBox="0 0 64 64" className="w-full h-full text-orange-500">
          <ellipse cx="32" cy="32" rx="28" ry="16" fill="currentColor" opacity="0.3"/>
          <ellipse cx="32" cy="32" rx="24" ry="12" fill="currentColor" opacity="0.5"/>
          <ellipse cx="32" cy="32" rx="20" ry="10" fill="currentColor"/>
          <rect x="26" y="26" width="12" height="12" rx="2" fill="white" opacity="0.8"/>
          <line x1="20" y1="32" x2="44" y2="32" stroke="white" strokeWidth="2" opacity="0.6"/>
          <line x1="32" y1="22" x2="32" y2="42" stroke="white" strokeWidth="2" opacity="0.6"/>
        </svg>
      );
    }
    
    if (category.includes('antiseptic') || category.includes('solution') || category.includes('cleanser')) {
      return (
        <svg viewBox="0 0 64 64" className="w-full h-full text-purple-500">
          <rect x="20" y="12" width="24" height="40" rx="4" fill="currentColor" opacity="0.3"/>
          <rect x="22" y="14" width="20" height="32" fill="currentColor" opacity="0.6"/>
          <rect x="26" y="8" width="12" height="8" rx="2" fill="currentColor"/>
          <circle cx="32" cy="26" r="4" fill="white" opacity="0.8"/>
          <rect x="26" y="34" width="12" height="3" fill="white" opacity="0.6"/>
          <rect x="26" y="40" width="12" height="3" fill="white" opacity="0.6"/>
          <rect x="28" y="46" width="8" height="2" fill="white" opacity="0.4"/>
        </svg>
      );
    }
    
    if (category.includes('system') || category.includes('device') || category.includes('tool') || 
        category.includes('therapy') || category.includes('chamber')) {
      return (
        <svg viewBox="0 0 64 64" className="w-full h-full text-indigo-500">
          <rect x="12" y="16" width="40" height="32" rx="6" fill="currentColor" opacity="0.3"/>
          <rect x="16" y="20" width="32" height="24" rx="4" fill="currentColor" opacity="0.6"/>
          <circle cx="32" cy="32" r="8" fill="currentColor"/>
          <circle cx="32" cy="32" r="4" fill="white"/>
          <rect x="52" y="28" width="8" height="8" rx="2" fill="currentColor" opacity="0.8"/>
          <line x1="44" y1="32" x2="52" y2="32" stroke="currentColor" strokeWidth="3"/>
          <rect x="18" y="22" width="6" height="3" fill="white" opacity="0.7"/>
          <rect x="18" y="27" width="6" height="3" fill="white" opacity="0.7"/>
        </svg>
      );
    }
    
    if (category.includes('measurement') || category.includes('documentation') || category.includes('photography')) {
      return (
        <svg viewBox="0 0 64 64" className="w-full h-full text-teal-500">
          <rect x="16" y="12" width="32" height="40" rx="4" fill="currentColor" opacity="0.3"/>
          <rect x="18" y="14" width="28" height="32" fill="currentColor" opacity="0.6"/>
          <rect x="20" y="16" width="24" height="16" fill="white" opacity="0.9"/>
          <circle cx="32" cy="24" r="3" fill="currentColor"/>
          <rect x="22" y="36" width="20" height="3" fill="white" opacity="0.8"/>
          <rect x="22" y="42" width="16" height="3" fill="white" opacity="0.6"/>
          <rect x="22" y="48" width="12" height="2" fill="white" opacity="0.4"/>
        </svg>
      );
    }
    
    // Default medical device icon
    return (
      <svg viewBox="0 0 64 64" className="w-full h-full text-gray-500">
        <rect x="16" y="16" width="32" height="32" rx="6" fill="currentColor" opacity="0.3"/>
        <rect x="20" y="20" width="24" height="24" rx="4" fill="currentColor" opacity="0.6"/>
        <circle cx="32" cy="32" r="6" fill="currentColor"/>
        <circle cx="32" cy="32" r="3" fill="white"/>
        <rect x="26" y="26" width="3" height="3" fill="white" opacity="0.7"/>
        <rect x="35" y="26" width="3" height="3" fill="white" opacity="0.7"/>
      </svg>
    );
  };

  const ProductCard = ({ product }: { product: Product }) => (
    <Card className="group hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-4">
        <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg mb-4 flex items-center justify-center border border-gray-200">
          {getProductImage(product)}
        </div>
        <div className="space-y-2">
          <h3 className="font-semibold text-lg truncate">{product.name}</h3>
          <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
          <div className="flex items-center justify-between">
            <Badge variant="secondary">{product.category}</Badge>
            <div className="flex items-center gap-1 text-sm text-yellow-600">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span>4.8</span>
            </div>
          </div>
          <div className="flex items-center justify-between pt-2">
            <span className="text-2xl font-bold text-blue-600">
              {formatCurrency(product.unitPrice)}
            </span>
            <span className="text-sm text-gray-500">SKU: {product.sku}</span>
          </div>
          <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button size="sm" variant="outline" className="w-full">
              <Edit className="h-4 w-4 mr-1" />
              Edit Product
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const ProductListItem = ({ product }: { product: Product }) => (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200">
            {getProductImage(product)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{product.name}</h3>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{product.description}</p>
                <div className="flex items-center gap-4 mt-2">
                  <Badge variant="secondary">{product.category}</Badge>
                  <span className="text-sm text-gray-500">SKU: {product.sku}</span>
                  <div className="flex items-center gap-1 text-sm text-yellow-600">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span>4.8</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold text-xl text-blue-600 mb-2">
                  {formatCurrency(product.unitPrice)}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline">
                    <Edit className="h-4 w-4 mr-1" />
                    Edit Product
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="aspect-square bg-gray-200 rounded-lg mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    // <div className="product-management-page">
    <div className="product-management-page min-h-screen bg-gray-50 flex">
      
      {/* Main Content Area */}
      <div className="flex-1 transition-all duration-300">

        {/* Products Content */}
        <div className="p-6 max-w-7xl mx-auto">

        {/* Product Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Products</p>
                  <p className="text-2xl font-bold text-gray-900">27</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Package2 className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Categories</p>
                  <p className="text-2xl font-bold text-gray-900">26</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Star className="h-6 w-6 text-yellow-600 fill-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Avg Rating</p>
                  <p className="text-2xl font-bold text-gray-900">4.8</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 text-xl font-bold">$</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Value</p>
                  <p className="text-2xl font-bold text-gray-900">$28,990.43</p>
                </div>
              </div>
            </CardContent>
          </Card>
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {(selectedCategories.length + selectedBrands.length + selectedTypes.length) > 0 && (
                <Badge className="ml-2" variant="secondary">
                  {selectedCategories.length + selectedBrands.length + selectedTypes.length}
                </Badge>
              )}
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <Card className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Category Filter */}
              <div>
                <h4 className="font-medium mb-3 flex items-center">
                  <Package className="h-4 w-4 mr-2" />
                  Categories
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {categories.map((category) => (
                    <div key={category} className="flex items-center space-x-2">
                      <Checkbox
                        id={`category-${category}`}
                        checked={selectedCategories.includes(category)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedCategories([...selectedCategories, category]);
                          } else {
                            setSelectedCategories(selectedCategories.filter(c => c !== category));
                          }
                        }}
                      />
                      <label htmlFor={`category-${category}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {category}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Brand Filter */}
              <div>
                <h4 className="font-medium mb-3 flex items-center">
                  <Star className="h-4 w-4 mr-2" />
                  Brands
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {brands.slice(0, 8).map((brand) => (
                    <div key={brand} className="flex items-center space-x-2">
                      <Checkbox
                        id={`brand-${brand}`}
                        checked={selectedBrands.includes(brand)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedBrands([...selectedBrands, brand]);
                          } else {
                            setSelectedBrands(selectedBrands.filter(b => b !== brand));
                          }
                        }}
                      />
                      <label htmlFor={`brand-${brand}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {brand}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Type Filter */}
              <div>
                <h4 className="font-medium mb-3 flex items-center">
                  <Package2 className="h-4 w-4 mr-2" />
                  Product Types
                </h4>
                <div className="space-y-2">
                  {types.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`type-${type}`}
                        checked={selectedTypes.includes(type)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedTypes([...selectedTypes, type]);
                          } else {
                            setSelectedTypes(selectedTypes.filter(t => t !== type));
                          }
                        }}
                      />
                      <label htmlFor={`type-${type}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {type}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-gray-600">
                Showing {filteredProducts.length} of {products.length} products
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedCategories([]);
                    setSelectedBrands([]);
                    setSelectedTypes([]);
                  }}
                >
                  Clear All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Active Filters Display */}
        {(selectedCategories.length > 0 || selectedBrands.length > 0 || selectedTypes.length > 0) && (
          <div className="flex flex-wrap gap-2">
            {selectedCategories.map((category) => (
              <Badge key={category} variant="secondary" className="flex items-center gap-1">
                {category}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => setSelectedCategories(selectedCategories.filter(c => c !== category))}
                />
              </Badge>
            ))}
            {selectedBrands.map((brand) => (
              <Badge key={brand} variant="secondary" className="flex items-center gap-1">
                {brand}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => setSelectedBrands(selectedBrands.filter(b => b !== brand))}
                />
              </Badge>
            ))}
            {selectedTypes.map((type) => (
              <Badge key={type} variant="secondary" className="flex items-center gap-1">
                {type}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => setSelectedTypes(selectedTypes.filter(t => t !== type))}
                />
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Products Display */}
      {filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </CardContent>
        </Card>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            : "space-y-4"
        }>
          {filteredProducts.map((product: Product) => (
            viewMode === 'grid' 
              ? <ProductCard key={product._id} product={product} />
              : <ProductListItem key={product._id} product={product} />
          ))}
          </div>
        )}
        </div>
      </div>
    </div>
    // {/* </div> */}
  );
}